import type {
  AdapterKind,
  CatalogEntry,
  CatalogListResponse,
  DashboardSnapshot,
  Incident,
  Service,
  StatuspagePreview,
  WebhookEvent,
  RegionSettings,
} from "./types";
import { authHeader } from "./auth";

const BASE = import.meta.env.VITE_API_BASE ?? "";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  for (const [k, v] of Object.entries(authHeader())) headers.set(k, v);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  snapshot: () => request<DashboardSnapshot>("/api/snapshot"),
  listServices: () => request<Service[]>("/api/services"),
  getService: (id: string) => request<Service>(`/api/services/${id}`),
  getServiceRegions: (id: string) =>
    request<RegionSettings>(`/api/services/${encodeURIComponent(id)}/regions`),
  updateServiceRegions: (id: string, regions: string[]) =>
    request<RegionSettings>(`/api/services/${encodeURIComponent(id)}/regions`, {
      method: "PUT",
      body: JSON.stringify({ regions }),
    }),
  createServiceRss: (input: {
    name: string;
    feedUrl: string;
    pollIntervalSeconds: number;
  }) =>
    request<Service>("/api/services", {
      method: "POST",
      body: JSON.stringify({ source: "rss", ...input }),
    }),
  createServiceStatuspage: (input: {
    name: string;
    feedUrl: string;
    pollIntervalSeconds: number;
  }) =>
    request<Service>("/api/services", {
      method: "POST",
      body: JSON.stringify({ source: "statuspage", ...input }),
    }),
  createServiceWebhook: (input: { name: string }) =>
    request<{ service: Service; signingSecret: string }>("/api/services", {
      method: "POST",
      body: JSON.stringify({ source: "webhook", ...input }),
    }),
  listCatalog: (filters: {
    q?: string;
    category?: string;
    adapter?: AdapterKind;
    enabled?: boolean;
  } = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== "") params.set(key, String(value));
    }
    const query = params.toString();
    return request<CatalogListResponse>(
      `/api/catalog${query ? `?${query}` : ""}`,
    );
  },
  getCatalogEntry: (id: string) => request<CatalogEntry>(`/api/catalog/${id}`),
  // Only the catalog id crosses the wire: the server owns the ingestion URL, so
  // a known provider can never be pointed at a URL chosen by the client.
  createServiceFromCatalog: (input: {
    catalogId: string;
    pollIntervalSeconds?: number;
  }) =>
    request<Service>("/api/services/from-catalog", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  previewStatuspage: (url: string) =>
    request<StatuspagePreview>("/api/admin/statuspage/preview", {
      method: "POST",
      body: JSON.stringify({ url }),
    }),
  listIncidents: (serviceId?: string) =>
    request<Incident[]>(
      `/api/incidents${serviceId ? `?service=${serviceId}` : ""}`,
    ),
  listWebhookEvents: () => request<WebhookEvent[]>("/api/webhook-events"),
  parseRss: (url: string) =>
    request<{
      title: string;
      entries: { title: string; date: string; status: string; sev: string }[];
      componentCount: number;
      sizeKb: number;
    }>("/api/admin/rss/parse", {
      method: "POST",
      body: JSON.stringify({ url }),
    }),
};
